import React, { useState } from 'react';
import {
    Card,
    CardContent,
    CardActionArea,
    IconButton,
    Typography,
    Box,
    Menu,
    MenuItem,
    Switch, Chip
} from '@mui/material';
import { MoreVert, Edit, Delete } from '@mui/icons-material';
import {AgentSkill, AgentSkillTypeInfo} from "../types/AgentSkillTypes";

interface AgentSkillEntityTileProps {
    skillInfo: AgentSkillTypeInfo;
    skill: AgentSkill;
    onSelect?: () => void;
    onConfigure?: (skill: AgentSkill) => void;
    onDelete?: (skill: AgentSkill) => void;
}

const AgentSkillEntityTile: React.FC<AgentSkillEntityTileProps> = ({
                                                           skillInfo,
                                                           skill,
                                                           onSelect,
                                                           onConfigure,
                                                           onDelete
                                                       }) => {
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const [isActive, setIsActive] = useState(skill.status === 'active');

    const IconComponent = skillInfo.icon;
    const menuOpen = Boolean(menuAnchor);

    const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setMenuAnchor(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
    };

    const handleToggle = () => {
        setIsActive(prev => !prev);
    };

    return (
        <Card
            sx={{
                height: 100,
                display: 'flex',
                alignItems: 'center',
                px: 2,
                '&:hover': {
                    boxShadow: 6
                }
            }}
        >
            {/* Left - Switch */}
            <Switch checked={isActive} onChange={handleToggle} sx={{ mr: 2 }} />

            {/* Center - Icon and Info */}
            <CardActionArea onClick={onSelect} sx={{ flex: 1, height: '100%' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ fontSize: 48, color: 'primary.main' }}>
                        <IconComponent />
                    </Box>
                    <Box>
                        <Typography variant="h6">{skillInfo.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {skillInfo.description}
                        </Typography>
                    </Box>
                    <Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {Object.entries(skill.configuration || {})
                                .filter(([key]) => key !== 'skill_name' && key !== 'skill_description')
                                .map(([key, value]) => (
                                <Chip
                                    sx={{
                                        maxWidth: 200,
                                        overflow: 'hidden',
                                        whiteSpace: 'nowrap',
                                        textOverflow: 'ellipsis'
                                    }}
                                    key={key}
                                    label={`${String(value)}`}
                                    size="small"
                                    variant="outlined"
                                />
                            ))}
                        </Box>
                    </Box>
                </CardContent>
            </CardActionArea>

            {/* Right - Menu */}
            <IconButton onClick={handleMenuOpen}>
                <MoreVert />
            </IconButton>
            <Menu anchorEl={menuAnchor} open={menuOpen} onClose={handleMenuClose}>
                <MenuItem
                    onClick={() => {
                        onConfigure?.(skill);
                        handleMenuClose();
                    }}
                >
                    <Edit fontSize="small" sx={{ mr: 1 }} />
                    Edit
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        onDelete?.(skill);
                        handleMenuClose();
                    }}
                >
                    <Delete fontSize="small" sx={{ mr: 1 }} />
                    Delete
                </MenuItem>
            </Menu>
        </Card>
    );
};

export default AgentSkillEntityTile;
